import { Box, Icon, IconButton, Stack } from '@mui/material';
import {
  AssessmentGroup,
  FilterKeys,
  GetGroupsQueryFilters,
  Group,
  GroupType,
  useGetGroupsQuery,
} from '@schooly/api';
import { SearchInput, SearchInputFilterIconButton } from '@schooly/components/filters';
import { useFlag } from '@schooly/hooks/use-flag';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { CrossIcon, SkeletonRowsComponent } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { NoSearchResultsFound } from '../../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { ModalPanel } from '../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import { AssessmentGroupFiltersPopup } from '../../AssessmentsPreviewModal/AssessmentGroupFiltersPopup';
import { InlineFiltersTags } from '../InlineFiltersTags';
import AssessmentsCreateModalGroupsItem from './AssessmentsCreateModalGroupsItem';

const PAGE_SIZE = 30;

const groupFilters: GetGroupsQueryFilters = {
  [FilterKeys.StaffHouse]: [],
  [FilterKeys.StudentHouse]: [],
  [FilterKeys.AgeGroup]: [],
  [FilterKeys.StaffStatus]: [],
  [FilterKeys.StudentStatus]: [],
  [FilterKeys.Subject]: [],
};

const ASSESSMENTS_GROUPS_FILTER_KEYS = [
  FilterKeys.StaffHouse,
  FilterKeys.StudentHouse,
  FilterKeys.AgeGroup,
  FilterKeys.StaffStatus,
  FilterKeys.StudentStatus,
  FilterKeys.Subject,
] as const;

type AssessmentsCreateModalGroupsRightSidebarProps = {
  selectedGroups: AssessmentGroup[];
  onCardClick: (group: Group) => void;
  onSetGroups: (groups: Group[]) => void;
  relationIds: string[];
  schoolId: string;
  dateParams: GetGroupsQueryFilters;
};

export const AssessmentsCreateModalGroupsRightSidebar: FC<
  AssessmentsCreateModalGroupsRightSidebarProps
> = ({ selectedGroups, onCardClick, relationIds, schoolId, dateParams }) => {
  const { formatMessage } = useIntl();

  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);

  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    params,
    setParams,
    fetchNextPage,
    isLoading,
    isFetching,
  } = useGetGroupsQuery({
    schoolId: schoolId,
    pageSize: PAGE_SIZE,
    filters: {
      ...groupFilters,
      ...dateParams,
      [FilterKeys.GroupTypes]: [GroupType.Subject, GroupType.TutorGroup],
    },
    relationIds,
    query: '',
  });

  const total = data?.pages[0]?.count;

  const hasFiltersApplied = ASSESSMENTS_GROUPS_FILTER_KEYS.some(
    (key) => !!params.filters[key]?.length,
  );

  const handleSetFiltersQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const handleSetFilters = useCallback(
    (filters: GetGroupsQueryFilters) => {
      setParams((p) => ({
        ...p,
        filters: {
          ...p.filters,
          ...filters,
        },
      }));
    },
    [setParams],
  );

  const handleResetFilters = useCallback(() => {
    const resetFilters = ASSESSMENTS_GROUPS_FILTER_KEYS.reduce<GetGroupsQueryFilters>(
      (acc, key) => {
        acc[key] = [];
        return acc;
      },
      {},
    );

    setParams((p) => ({ ...p, filters: { ...p.filters, ...resetFilters } }));
  }, [setParams]);

  const loaderRef = useInfiniteScroll(isFetching, fetchNextPage, hasNextPage);

  const groups = useMemo(() => {
    const selectedGroupIds = selectedGroups.map(({ id }) => id);
    return data?.pages.reduce<Group[]>(
      (prev, curr) => [...prev, ...curr.results.filter((g) => !selectedGroupIds.includes(g.id))],
      [],
    );
  }, [data?.pages, selectedGroups]);

  const renderList = () => {
    return (
      <>
        {!isLoading && !groups?.length && <NoSearchResultsFound />}

        {groups?.length && (
          <Stack>
            {groups?.map((g) => {
              return (
                <AssessmentsCreateModalGroupsItem
                  key={g.id}
                  onClick={() => onCardClick(g)}
                  group={g}
                  generateHref={(id) => `/groups/${id}`}
                />
              );
            })}
          </Stack>
        )}

        {isLoading && <SkeletonRowsComponent rowCount={PAGE_SIZE} />}

        {hasNextPage && (
          <>
            <div ref={loaderRef} />
            {isFetchingNextPage && (
              <SkeletonRowsComponent
                rowCount={Math.min(
                  PAGE_SIZE,
                  total && data ? total - data.pages.length * PAGE_SIZE : PAGE_SIZE,
                )}
              />
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <ModalHeader title={formatMessage({ id: 'assessments-Groups' })} active />
      <Box p={2.5}>
        <Stack gap={1}>
          <Stack justifyContent="center" position="relative">
            <SearchInput
              placeholder={formatMessage({ id: 'people-Search' })}
              onChangeText={handleSetFiltersQuery}
              value={params.query || ''}
              canClear={false}
              InputProps={{
                endAdornment: (
                  <Stack pl={0.75} gap={0.5} pr={1} flexDirection="row">
                    {!!params.query && (
                      <IconButton
                        sx={(theme) => ({ '&:hover': { color: theme.palette.text.primary } })}
                        inverse
                        onClick={() => handleSetFiltersQuery('')}
                      >
                        <CrossIcon />
                      </IconButton>
                    )}
                    <Icon
                      sx={{
                        position: 'relative',
                        '.MuiButtonBase-root': { right: 0 },
                        overflow: 'visible',
                      }}
                    >
                      <SearchInputFilterIconButton
                        hasAppliedIndicator={hasFiltersApplied}
                        onClick={openFilterModal}
                      />
                    </Icon>
                  </Stack>
                ),
              }}
            />
          </Stack>
          <InlineFiltersTags
            onResetFilters={handleResetFilters}
            onOpenFilters={openFilterModal}
            filters={params.filters}
          />
        </Stack>
      </Box>
      <ModalPanel sx={{ pt: 0, flex: '1 1 auto', overflowY: 'auto' }} active>
        {renderList()}
      </ModalPanel>
      {filtersModalVisible && (
        <AssessmentGroupFiltersPopup
          onClose={closeFiltersModal}
          defaultFilters={groupFilters}
          onSetFilters={handleSetFilters}
          filters={params.filters}
        />
      )}
    </Stack>
  );
};
